import { FC } from 'react';
import { Box, useMediaQuery, useTheme, SelectChangeEvent, Button } from '@mui/material';
import { CustomDropdown, DotsLoader } from '@components';
import { Referrer } from '@api/types/commissions';
import { ReactComponent as FilterIcon } from '../../../../../assets/icons/filter_icon.svg';
import RefreshIcon from '@mui/icons-material/Refresh';

interface CommissionFiltersProps {
  referrers: Referrer[];
  referrer: string;
  onFilterChange: (referrer: string) => void;
  isRefreshingCommissions: boolean;
  onRefreshCommissions: () => void;
}

const CommissionFilters: FC<CommissionFiltersProps> = ({
  referrers,
  referrer,
  onFilterChange,
  isRefreshingCommissions,
  onRefreshCommissions,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const referrerOptions = [
    { id: 'All', label: 'All Referrers' },
    ...referrers.map((r) => ({ id: r.id, label: r.name })),
  ];

  const handleReferrerChange = (event: SelectChangeEvent) => {
    onFilterChange(event.target.value as string);
  };

  return (
    <Box
      sx={{
        padding: '0.8rem',
        backgroundColor: 'white',
        borderRadius: '0.8rem',
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between',
        alignItems: isMobile ? 'stretch' : 'center',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          gap: '0.5rem',
          alignItems: 'center',
          color: '#162936',
          fontWeight: 700,
          fontSize: '1rem',
          lineHeight: '1.375rem',
          paddingY: '0.5rem',
        }}
      >
        <FilterIcon />
        Filters
      </Box>

      <Box sx={{ display: 'flex', gap: '0.8rem' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: '0.8rem',
          }}
        >
          <CustomDropdown
            selectedOption={referrer}
            dropdownOptions={referrerOptions}
            handleChange={handleReferrerChange}
          />
        </Box>

        <Button
          disableElevation
          disableFocusRipple
          disableRipple
          disableTouchRipple
          variant="contained"
          color="primary"
          onClick={onRefreshCommissions}
          disabled={isRefreshingCommissions}
          startIcon={isRefreshingCommissions ? <></> : <RefreshIcon sx={{ color: 'white' }} />}
          sx={{
            textTransform: 'none',
            fontSize: '0.875rem',
            fontWeight: 700,
            lineHeight: '1.125rem',
            padding: '0.5rem 0.5rem',
            width: '140px',
            borderRadius: '0.5rem',
            backgroundColor: '#2F736E',
            '&.Mui-disabled': {
              backgroundColor: '#2F736E',
              cursor: 'not-allowed',
            },
          }}
        >
          {isRefreshingCommissions ? <DotsLoader size={4} /> : 'Recalculate'}
        </Button>
      </Box>
    </Box>
  );
};

export default CommissionFilters;
